import { useEffect, useState } from "react";
import { Send, Phone, Check, AlertCircle, Ticket } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  seats: 1,
  message: "",
};

export default function ReservationForm() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");
  const [remaining, setRemaining] = useState(8);
  const [isFull, setIsFull] = useState(false);

  useEffect(() => {
    const refresh = async () => {
      try {
        const r = await fetch(`${API_BASE}/api/event`, { cache: "no-store" });
        if (r.ok) {
          const d = await r.json();
          setRemaining(d?.remaining_seats ?? 8);
          setIsFull(Boolean(d?.is_full));
        }
      } catch (_e) {
        /* keep previous value on network error */
      }
    };
    refresh();
    const interval = setInterval(refresh, 15000);
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [success]);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || isFull) return;
    setError("");

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      seats: Number(form.seats) || 1,
      message: form.message.trim(),
    };

    if (!payload.name || !payload.email || !payload.phone) {
      setError("Merci de renseigner votre nom, email et téléphone.");
      return;
    }
    if (payload.seats < 1 || payload.seats > 8) {
      setError("Le nombre de places doit être compris entre 1 et 8.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/reservations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(
          data?.detail ||
            "Une erreur est survenue. Merci de réessayer ou d'appeler le 06.47.88.31.57."
        );
      } else {
        setSuccess(data);
        setForm(EMPTY);
        // Notify the rest of the page that availability changed.
        try {
          window.dispatchEvent(
            new CustomEvent("reservation-created", { detail: data })
          );
        } catch (_e) {
          /* no-op */
        }
      }
    } catch (_e) {
      setError(
        "Connexion impossible. Merci de réessayer ou d'appeler le 06.47.88.31.57."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="reservation-success"
        className="bg-white border border-[var(--line)] rounded-3xl p-8 sm:p-10 h-full flex flex-col items-start justify-center shadow-[0_2px_24px_-12px_rgba(41,37,36,0.10)]"
      >
        <div className="w-14 h-14 rounded-full bg-[#eaf3ec] text-[#2f6b3d] flex items-center justify-center mb-5">
          <Check size={26} />
        </div>
        <p className="text-xs uppercase tracking-[0.22em] text-[var(--accent)] font-medium mb-2">
          Réservation reçue
        </p>
        <h3 className="font-serif text-3xl sm:text-4xl text-[var(--text-primary)] leading-tight mb-4">
          Merci ! Nous revenons vers vous très vite.
        </h3>
        <p className="text-[var(--text-secondary)] leading-relaxed mb-2">
          Votre demande a bien été enregistrée. Nous vous recontacterons par
          email ou téléphone pour confirmer votre place et vous communiquer
          les modalités de paiement.
        </p>
        <p className="text-[var(--text-secondary)] leading-relaxed">
          {success.seats} place{success.seats > 1 ? "s" : ""} réservée
          {success.seats > 1 ? "s" : ""} · il reste {success.remaining_seats}{" "}
          place{success.remaining_seats > 1 ? "s" : ""} disponible
          {success.remaining_seats > 1 ? "s" : ""}.
        </p>

        <div
          data-testid="entry-pass-notice"
          className="mt-6 w-full flex items-start gap-3 px-5 py-4 rounded-2xl bg-[var(--bg-secondary)] border border-dashed border-[var(--accent)]/40"
        >
          <div className="shrink-0 w-9 h-9 rounded-full bg-white text-[var(--accent)] flex items-center justify-center">
            <Ticket size={16} />
          </div>
          <div className="text-sm leading-relaxed text-[var(--text-primary)]">
            <p className="font-medium text-[var(--text-primary)] mb-0.5">
              Votre laissez-passer
            </p>
            <p className="text-[var(--text-secondary)]">
              Conservez cette page (capture d'écran ou impression) et
              présentez-la le jour de l'atelier pour valider votre venue.
              Le paiement s'effectuera en magasin.
            </p>
          </div>
        </div>
        <a
          href="tel:+33647883157"
          className="mt-7 inline-flex items-center gap-2 text-[var(--text-primary)] hover:text-[var(--accent)] transition-colors"
        >
          <Phone size={16} />
          <span className="font-serif text-lg tracking-wide">
            06.47.88.31.57
          </span>
        </a>
        <button
          type="button"
          data-testid="new-reservation"
          onClick={() => setSuccess(null)}
          className="mt-6 text-sm underline text-[var(--text-secondary)] hover:text-[var(--accent)] transition-colors"
        >
          Faire une nouvelle réservation
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="reservation-form"
      className="bg-white border border-[var(--line)] rounded-3xl p-7 sm:p-9 h-full flex flex-col shadow-[0_2px_24px_-12px_rgba(41,37,36,0.10)]"
    >
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.22em] text-[var(--accent)] font-medium">
          Formulaire de réservation
        </p>
        <h3 className="font-serif text-3xl sm:text-4xl mt-2 leading-tight text-[var(--text-primary)]">
          Réservez votre place
        </h3>
        <p className="mt-2 text-sm text-[var(--text-secondary)]">
          Tous les champs marqués d'un * sont requis.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <Field label="Nom complet *" htmlFor="name">
          <input
            id="name"
            data-testid="form-name"
            type="text"
            value={form.name}
            onChange={update("name")}
            required
            autoComplete="name"
            className={inputCls}
            placeholder="Marie Vasseur"
          />
        </Field>
        <Field label="Email *" htmlFor="email">
          <input
            id="email"
            data-testid="form-email"
            type="email"
            value={form.email}
            onChange={update("email")}
            required
            autoComplete="email"
            className={inputCls}
            placeholder="vous@exemple.fr"
          />
        </Field>
        <Field label="Téléphone *" htmlFor="phone">
          <input
            id="phone"
            data-testid="form-phone"
            type="tel"
            value={form.phone}
            onChange={update("phone")}
            required
            autoComplete="tel"
            className={inputCls}
            placeholder="06 12 34 56 78"
          />
        </Field>
        <Field label="Nombre de places *" htmlFor="seats">
          <select
            id="seats"
            data-testid="form-seats"
            value={form.seats}
            onChange={update("seats")}
            className={inputCls}
            disabled={isFull}
          >
            {Array.from({ length: Math.max(remaining, 1) }, (_, i) => i + 1).map(
              (n) => (
                <option key={n} value={n}>
                  {n} place{n > 1 ? "s" : ""}
                </option>
              )
            )}
          </select>
        </Field>
      </div>

      <Field label="Message (optionnel)" htmlFor="message">
        <textarea
          id="message"
          data-testid="form-message"
          value={form.message}
          onChange={update("message")}
          rows={3}
          className={`${inputCls} resize-none`}
          placeholder="Une précision, une question ?"
        />
      </Field>

      {error && (
        <div
          data-testid="form-error"
          className="mt-4 flex items-start gap-2 text-sm text-[#9b3a2b] bg-[#fbeae7] border border-[#f0c8c0] rounded-xl px-4 py-3"
        >
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-xs text-[var(--text-secondary)]">
          En validant, vous acceptez d'être recontacté(e) pour confirmer votre
          place.
        </p>
        <button
          type="submit"
          data-testid="form-submit"
          disabled={submitting || isFull}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium transition-all duration-200 active:scale-[0.98]"
        >
          {submitting ? "Envoi…" : isFull ? "Complet" : "Confirmer la réservation"}
          {!submitting && !isFull && <Send size={16} />}
        </button>
      </div>
    </form>
  );
}

const inputCls =
  "w-full bg-[var(--bg-secondary)] border border-transparent focus:border-[var(--accent)] focus:bg-white outline-none rounded-xl px-4 py-3 text-[15px] text-[var(--text-primary)] placeholder:text-[var(--text-secondary)]/60 transition-colors disabled:opacity-60";

function Field({ label, htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="block text-[11px] uppercase tracking-[0.16em] text-[var(--text-secondary)] mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
